import { apiRequest } from '@department-of-veterans-affairs/platform-utilities/api';
import { useFeatureToggle } from 'platform/utilities/feature-toggles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  DIRECT_LINE_CONNECTION_ERROR_EVENT,
  DIRECT_LINE_CONNECTION_RECOVERED_EVENT,
  DIRECT_LINE_TOKEN_EXPIRED_EVENT,
} from '../shared/directLine';
import {
  getAlertTargetTs,
  parseTTLSeconds,
  shouldShowExpiryAlertAt,
} from '../utils/expiry';
import {
  getVaSessionExpiration,
  hasVaSessionExpired,
} from '../utils/sessionPolicy';
import { getDeviceProfileHeaders } from '../utils/deviceProfile';
import { COMPLETE, ERROR, LOADING } from '../utils/loadingStatus';
import { logErrorToDatadog } from '../utils/logging';
import retryOnce from '../utils/retryOnce';
import {
  clearBotSessionStorage,
  getAuthenticatedConversationIdKey,
  getConversationIdKey,
  getLoggedInFlow,
  getTokenExpiresAt,
  getTokenKey,
  setConversationIdKey,
  setLoggedInFlow,
  setTokenExpiresAt,
  setTokenKey,
} from '../utils/sessionStorage';

function clearExpiredDirectLineSession() {
  try {
    sessionStorage.removeItem('va-bot.token');
    sessionStorage.removeItem('va-bot.conversationId');
    sessionStorage.removeItem('va-bot.tokenExpiresAt');
    sessionStorage.removeItem('va-bot.conversationToken');
    sessionStorage.removeItem('va-bot.authenticatedConversationId');
  } catch (e) {
    // no-op
  }
}

function clearSessionForNewConversation() {
  clearExpiredDirectLineSession();
  try {
    sessionStorage.removeItem('va-bot.recentUtterances');
    sessionStorage.removeItem('va-bot.isTrackingUtterances');
    sessionStorage.removeItem('va-bot.skillEventValue');
  } catch (e) {
    // no-op
  }
}

async function getToken({
  forceNew = false,
  hasChatEnded = () => false,
  isClaimsAuthEnabled = false,
  resetExpiredCb = null,
  setCode,
  setExpiresAtCb = null,
  setFeaturesCb = null,
  setLoadingStatus,
  setToken,
}) {
  try {
    const response = await retryOnce(() =>
      apiRequest('/chatbot/token', {
        headers: getDeviceProfileHeaders(isClaimsAuthEnabled),
        method: 'POST',
      }),
    );

    // The chat can end while this request is in flight, and logout clears the
    // deadline too. Drop the response rather than reopen or re-persist it.
    if (hasChatEnded()) return false;

    if (!response?.token || !response?.conversationId) {
      throw new Error(
        'Chatbot token response is missing Direct Line credentials',
      );
    }

    const existingConversationId = getConversationIdKey();
    const existingToken = getTokenKey();
    if (forceNew || !existingConversationId || !existingToken) {
      setConversationIdKey(response.conversationId);
      setTokenKey(response.token);
      setToken(response.token);
    } else {
      setToken(existingToken);
    }

    if (response.code) {
      // The code is single-use and only needs to reach the bot once for
      // this authentication -- keep it in memory just long enough to send,
      // no need to persist it.
      setCode(response.code);
    }

    // The resolved authenticated experiences, decided server-side. Absent or
    // malformed means none: a slow rollout fails closed, and this also keeps
    // the frontend a no-op until vets-api starts returning the field.
    if (typeof setFeaturesCb === 'function') {
      setFeaturesCb(Array.isArray(response.features) ? response.features : []);
    }

    const ttl = parseTTLSeconds(response.expires_in ?? response.expiresIn);
    const expiresAt = Date.now() + ttl * 1000;
    setTokenExpiresAt(expiresAt);
    if (typeof setExpiresAtCb === 'function') {
      setExpiresAtCb(expiresAt);
    }

    if (typeof resetExpiredCb === 'function' && !hasVaSessionExpired()) {
      resetExpiredCb(false);
    }
    setLoadingStatus(COMPLETE);
    return true;
  } catch (ex) {
    const error = new Error('Could not retrieve chatbot token');
    logErrorToDatadog(true, error.message, error);
    if (!hasChatEnded()) setLoadingStatus(ERROR);
    return false;
  }
}

async function fetchSignInCode(setCode, setFeatures, isClaimsAuthEnabled) {
  try {
    const response = await retryOnce(() =>
      apiRequest('/chatbot/token', {
        headers: getDeviceProfileHeaders(isClaimsAuthEnabled),
        method: 'POST',
      }),
    );
    // Signing in can change which experiences resolve, and this branch is the
    // one that keeps the conversation instead of re-entering getToken. The
    // features go before the code. A new code re-sends startConversation with
    // whatever features we're holding right then, so a conversation that never
    // had any would keep none.
    if (typeof setFeatures === 'function') {
      setFeatures(Array.isArray(response?.features) ? response.features : []);
    }
    if (response?.code) {
      setCode(response.code);
    }
  } catch (ex) {
    const error = new Error('Could not retrieve chatbot sign-in code');
    logErrorToDatadog(true, error.message, error);
  }
}

// The platform clears `sessionExpiration` while it fetches a new deadline, so
// a missing value is re-checked on this cadence rather than believed.
const SESSION_RECHECK_MS = 1000;

const selectIsSignedIn = state =>
  Boolean(state?.user?.login?.currentlyLoggedIn);

const MAX_AUTOMATIC_REFRESH_ATTEMPTS = 3;
const REFRESH_BACKOFF_BASE_MS = 1000;

export default function useChatbotToken() {
  const [token, setToken] = useState('');
  const [code, setCode] = useState('');
  const [features, setFeatures] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(LOADING);
  const [expired, setExpired] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const refreshTimerRef = useRef(null);
  const refreshInFlightRef = useRef(false);
  const automaticRefreshAttemptsRef = useRef(0);
  // The conversation we already asked for a code for. The effect below re-runs
  // while the profile loads, so this keeps it to one request per conversation.
  const identityRequestedForRef = useRef(null);
  // Set when a chat ends; only the user starting a new chat clears it. A ref so
  // requests and retries already under way see the current value.
  const chatEndedRef = useRef(false);
  // The platform deletes `sessionExpiration` while extending a session, so the
  // store is the only reliable signal of whether someone is signed in.
  const isSignedIn = useSelector(selectIsSignedIn);
  const { useToggleValue, TOGGLE_NAMES } = useFeatureToggle();
  const isSessionPersistenceEnabled = useToggleValue(
    TOGGLE_NAMES.virtualAgentChatbotSessionPersistenceEnabled,
  );
  // Read here as well as server-side so no profile is derived at all while the
  // kill switch is off. vets-api re-checks it as the enforcing gate.
  const isClaimsAuthEnabled = useToggleValue(
    TOGGLE_NAMES.vaChatbotClaimsEnable,
  );

  const clearFailedRefreshState = useCallback(() => {
    clearExpiredDirectLineSession();
    setToken('');
    setCode('');
    setFeatures([]);
    setExpiresAt(null);
    setExpired(true);
    setLoadingStatus(ERROR);
  }, []);

  const endExpiredSession = useCallback(() => {
    chatEndedRef.current = true;
    clearTimeout(refreshTimerRef.current);
    setExpired(true);
    setLoadingStatus(COMPLETE);
  }, []);

  const refreshToken = useCallback(
    async (automatic = false) => {
      if (refreshInFlightRef.current || chatEndedRef.current) return;

      if (automatic) {
        if (
          automaticRefreshAttemptsRef.current >= MAX_AUTOMATIC_REFRESH_ATTEMPTS
        ) {
          clearFailedRefreshState();
          return;
        }

        const delay =
          REFRESH_BACKOFF_BASE_MS * 2 ** automaticRefreshAttemptsRef.current;
        automaticRefreshAttemptsRef.current += 1;
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(() => refreshToken(false), delay);
        return;
      }

      refreshInFlightRef.current = true;
      setLoadingStatus(LOADING);
      setExpired(true);

      try {
        const refreshed = await getToken({
          forceNew: true,
          hasChatEnded: () => chatEndedRef.current,
          isClaimsAuthEnabled,
          resetExpiredCb: setExpired,
          setCode,
          setExpiresAtCb: setExpiresAt,
          setFeaturesCb: setFeatures,
          setLoadingStatus,
          setToken,
        });
        if (!refreshed && !chatEndedRef.current) {
          clearFailedRefreshState();
        }
      } finally {
        refreshInFlightRef.current = false;
      }
    },
    [clearFailedRefreshState, isClaimsAuthEnabled],
  );

  useEffect(() => {
    // vets-website sets this flag before handing the browser to the sign-in
    // redirect, so a fresh mount knows "the user just came back from signing in
    const returningFromSignIn = getLoggedInFlow() === 'true';

    clearBotSessionStorage();

    if (returningFromSignIn) {
      setLoggedInFlow('false');
    }

    if (!isSessionPersistenceEnabled) {
      refreshToken();
      return;
    }

    const existingConversationId = getConversationIdKey();
    const existingToken = getTokenKey();
    if (existingConversationId && existingToken) {
      const storedExpiresAt = getTokenExpiresAt();

      if (hasVaSessionExpired()) {
        clearSessionForNewConversation();
        setToken(existingToken);
        endExpiredSession();
        return;
      }

      // Nothing refreshes the token between page loads, so a stored token
      // past its lifetime really is dead and the conversation cannot resume.
      if (shouldShowExpiryAlertAt(storedExpiresAt)) {
        clearSessionForNewConversation();
        setToken(existingToken);
        endExpiredSession();
        return;
      }

      // A refresh mints a new token and gets a code alongside it, so these
      // paths need nothing extra.
      if (!storedExpiresAt) {
        refreshToken();
        return;
      }

      setExpiresAt(storedExpiresAt);
      setExpired(false);
      setToken(existingToken);
      setLoadingStatus(COMPLETE);

      // The only branch that keeps a conversation without fetching, so it is
      // the only one that has to go and get a code of its own. Nothing else
      // here re-enters `getToken`, so there is no second request to race.
      //
      // The sign-in flag only gets set by the login button inside the chat, so
      // it misses someone who signs in from the top of the page, and a start
      // that never reached the bot. What matters is whether the bot ever got a
      // code for this conversation. A plain refresh of one it did get a code
      // for fetches nothing.
      const needsIdentity =
        isSignedIn &&
        getAuthenticatedConversationIdKey() !== existingConversationId;
      if (
        (returningFromSignIn || needsIdentity) &&
        identityRequestedForRef.current !== existingConversationId
      ) {
        identityRequestedForRef.current = existingConversationId;
        fetchSignInCode(setCode, setFeatures, isClaimsAuthEnabled);
      }
      return;
    }

    refreshToken();
  }, [
    endExpiredSession,
    isClaimsAuthEnabled,
    isSessionPersistenceEnabled,
    isSignedIn,
    refreshToken,
  ]);

  useEffect(() => {
    const onConnErr = () => refreshToken(true);
    // Direct Line only reports this once its own refresh loop has been
    // refused, so the conversation is genuinely unrecoverable.
    const onTokenExpired = () => endExpiredSession();
    const onConnRecovered = () => {
      automaticRefreshAttemptsRef.current = 0;
    };

    window.addEventListener(DIRECT_LINE_CONNECTION_ERROR_EVENT, onConnErr);
    window.addEventListener(DIRECT_LINE_TOKEN_EXPIRED_EVENT, onTokenExpired);
    window.addEventListener(
      DIRECT_LINE_CONNECTION_RECOVERED_EVENT,
      onConnRecovered,
    );
    return () => {
      window.removeEventListener(DIRECT_LINE_CONNECTION_ERROR_EVENT, onConnErr);
      window.removeEventListener(
        DIRECT_LINE_TOKEN_EXPIRED_EVENT,
        onTokenExpired,
      );
      window.removeEventListener(
        DIRECT_LINE_CONNECTION_RECOVERED_EVENT,
        onConnRecovered,
      );
    };
  }, [endExpiredSession, refreshToken]);

  // Signed out, the token's lifetime is the chat's limit. Signed in it means
  // nothing: DirectLineJS keeps refreshing that token underneath us.
  useEffect(() => {
    if (!isSessionPersistenceEnabled || isSignedIn) return undefined;

    const target = getAlertTargetTs(expiresAt || getTokenExpiresAt());
    if (!target) return undefined;

    const delay = Math.max(0, target - Date.now());
    const timerId = setTimeout(endExpiredSession, delay);

    return () => clearTimeout(timerId);
  }, [endExpiredSession, expiresAt, isSessionPersistenceEnabled, isSignedIn]);

  // Re-reads the deadline when the timer fires instead of trusting the value
  // captured when it was armed, so extending the session re-arms the timer.
  useEffect(() => {
    if (!isSignedIn) return undefined;

    let timerId = null;

    const scheduleSessionCheck = () => {
      const target = getVaSessionExpiration();

      // Absent while the store still says signed in means the platform is
      // mid-extend. Wait for the new deadline instead of ending the chat.
      if (!target) {
        timerId = setTimeout(scheduleSessionCheck, SESSION_RECHECK_MS);
        return;
      }

      const delay = target - Date.now();
      if (delay <= 0) {
        endExpiredSession();
        return;
      }

      timerId = setTimeout(scheduleSessionCheck, delay);
    };

    scheduleSessionCheck();

    return () => clearTimeout(timerId);
  }, [endExpiredSession, isSignedIn]);

  // Timers do not fire reliably in a backgrounded tab, so re-evaluate
  // whenever it comes back to the foreground.
  useEffect(() => {
    const checkExpiry = () => {
      if (hasVaSessionExpired()) {
        endExpiredSession();
        return;
      }

      // Signed in there is no token deadline to re-check -- see above.
      if (isSignedIn || !isSessionPersistenceEnabled) return;

      if (shouldShowExpiryAlertAt(getTokenExpiresAt())) {
        endExpiredSession();
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkExpiry();
      }
    };

    window.addEventListener('focus', checkExpiry);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', checkExpiry);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [
    endExpiredSession,
    isSessionPersistenceEnabled,
    isSignedIn,
    refreshToken,
  ]);

  useEffect(() => {
    function onReset() {
      clearSessionForNewConversation();
      automaticRefreshAttemptsRef.current = 0;
      // A response still in flight belongs to the ended chat, so leave it ended
      // until that request settles.
      if (!refreshInFlightRef.current) chatEndedRef.current = false;
      refreshToken();
    }

    window.addEventListener('va-chatbot-reset', onReset);
    return () => window.removeEventListener('va-chatbot-reset', onReset);
  }, [refreshToken]);

  return { token, code, features, loadingStatus, expired };
}
